import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Dropdown, MenuProps, Segmented } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { Button, Typography } from '@atoms';
import { axiosInstance } from 'utils';
import MoreIcon from "@assets/icons/more.svg";
import CandleImage from "@assets/images/candle.png";
import RemoveIcon from "@assets/icons/remove-red.svg";

import { candleBlurStyles, CandlePeopleName, Divider, SmallBadge } from './tributes.styles';
import { DeleteModal } from '@molecules';
import { toast } from 'react-toastify';
import { useStoryDataContext } from "contexts/StoryContext";
import { useTributeNotificationsDataContext } from "../../../contexts/NotificationsContext";
import { markTributeNotificationsListRead } from "../../../api/others";

function Tributes() {
  const {tributeNotifications, markListRead} = useTributeNotificationsDataContext();
  const { t } = useTranslation();
  const { story, isCreator, tributes, setTributes} = useStoryDataContext();
  const id = story?.uuid;
  const [ selectType, setSelectType ] = useState<string>(t("confirmed"))
  const [ deleteTribute, setDeleteTribute ] = useState(false);
  const [ currentUIID, setCurrentUIID ] = useState("");
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");

  const newStoryTributes = tributeNotifications.filter(n => n.tribute_to_story.uuid === story?.uuid && !n.is_read);

  const filteredTributes = tributes.filter((el) => {
    if (selectType === t("new") || selectType.toLowerCase() === "new") {
      return el.status === "new" || el.status === "viewed";
    }
    if (selectType === t("confirmed") || selectType.toLowerCase() === "confirmed") {
      return el.status !== "new" && el.status !== "viewed";
    }
    return true;
  }) || [];

  const deleteItem: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "150px",
            cursor: "pointer",
          }}
          className="ant-dropdown-menu-title-content"
        >
          <Typography
            as="label"
            className="ant-dropdown-menu-title-content"
            style={{ color: "#990B0B", cursor: "pointer" }}
          >
            {t("delete")}
          </Typography>
          <img src={RemoveIcon} alt="remove" />
        </span>
      ),
    },
  ];

  // @ts-ignore
  const handleConfirmTribute = async (e) => {
    const tributeid = e.currentTarget.dataset.tributeid;
    const status = e.currentTarget.dataset.status;

    try {
      const payload = {
        uuid: tributeid,
        status,
      };

      await axiosInstance.patch(`tributes/${tributeid}/`, payload);

      try {
        const tribures = await axiosInstance.get(`tributes?story=${id}`);
        setTributes(tribures.data.results);
      } catch (err) {
        console.error("Ошибка при загрузке tributes:", err);
      }
    } catch (err) {
      console.error("Ошибка при загрузке данных:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`tributes/${currentUIID}/`);

      try {
        const tribures = await axiosInstance.get(`tributes?story=${id}`);
        setTributes(tribures.data.results);
      } catch (err) {
        console.error("Ошибка при загрузке tributes:", err);
      }
    } catch (err) {
      console.error("Ошибка при загрузке данных:", err);
      // @ts-ignore
      toast.error(err.response.data.detail, {
        position: "bottom-right",
        theme: "colored",
      });
    } finally {
      setDeleteTribute(false)
    }
  }

  const handleSegmentedChange = async (val: string) => {
    setSelectType(val);

    if (val === "new") {
      if (!newStoryTributes.length) return;

      try {
        await markTributeNotificationsListRead(newStoryTributes.map(n => n.uuid));
        markListRead(newStoryTributes.map(n => n.uuid))
      } catch (error) {
        //
      }
    }
  };

  if (!(tab === null || tab === "tributes") || !story?.enable_tribute) return <></>;

  return (
    <div className="tributes-list" style={{width: "360px"}}>
      <DeleteModal
        title={t("deleteTribute")}
        text={t("approveDeleteTribute")}
        open={deleteTribute}
        setOpen={setDeleteTribute}
        onOk={handleDelete}
      />
      {isCreator && (
        <Segmented
          value={selectType}
          onChange={handleSegmentedChange}
          options={[
            { label: t("confirmed"), value: "confirmed" },
            {
              label: <SmallBadge count={newStoryTributes.length}>{t("new")}</SmallBadge>,
              value: "new",
            },
          ]}
          block
        />
      )}
      {filteredTributes.length === 0 && (
        <Typography as="h4" style={{marginTop: "16px", textAlign: "center"}}>{t(isCreator ? "dontTribute" : "turnLightToLeave")}</Typography>
      )}
      {filteredTributes.map((el) => (
          <div
            className="candle"
            style={{
              padding: "6px",
              borderRadius: "8px",
              background: "#F9F2EC",
              border: "1px solid #E4D7CD",
              marginTop: "8px",
              opacity: el.status === "rejected" ? 0.5 : 1,
            }}
            key={el.uuid}
          >
            <div
              style={{
                display: "flex",
                gap: "24px",
                justifyContent: "space-between",
              }}
            >
              <img
                src={CandleImage}
                alt="candle"
                style={{borderRadius: "6px"}}
              />

              <div
                className="candle-info"
                style={{position: "relative", width: "100%"}}
              >
                <div
                  className="candle-people"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "4px",
                    gap: "8px",
                  }}
                >
                  {el.user.image ? (
                    <Avatar
                      size={32}
                      icon={<img src={el.user.image} alt="images"/>}
                    />
                  ) : (
                    <Avatar size={32} icon={<UserOutlined/>}/>
                  )}
                  <CandlePeopleName>
                    <Typography as="h4">{el.user.username}</Typography>
                    {el.status === "approved" && isCreator && (
                      <Dropdown
                        menu={{
                          items: deleteItem,
                          onClick: async (e) => {
                            setCurrentUIID(el.uuid)
                            setDeleteTribute(true)
                          },
                        }}
                        trigger={["click"]}
                      >
                        <Typography
                          style={{
                            color: "#391D0A",
                            cursor: "pointer",
                            display: "flex",
                            gap: 4,
                            alignItems: "start",
                          }}
                        >
                          <img src={MoreIcon} alt="more"/>
                        </Typography>
                      </Dropdown>
                    )}
                  </CandlePeopleName>
                </div>
                <Typography
                  className="candle-message"
                  style={el.status === "new" ? candleBlurStyles : undefined}
                >
                  {el.message}
                </Typography>
                <div style={{marginBottom: 8}}>
                  <Typography as="label">
                    {moment(el.created_at).format("DD.MM.YYYY")}
                  </Typography>
                </div>
                {el.status === "new" && (
                  <Button
                    onClick={handleConfirmTribute}
                    data-status="viewed"
                    data-tributeId={el.uuid}
                    variant="secondary"
                    style={{
                      position: "absolute",
                      bottom: "26px",
                      left: "40px",
                    }}
                  >
                    Посмотреть
                  </Button>
                )}
              </div>
            </div>
            {(el.status === "new" || el.status === "viewed") && <Divider/>}

            {(el.status === "new" || el.status === "viewed") && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <Button
                  onClick={async () => {
                    try {
                      await axiosInstance.delete(`tributes/${el.uuid}/`);

                      try {
                        const tribures = await axiosInstance.get(`tributes?story=${id}`);
                        setTributes(tribures.data.results);
                      } catch (err) {
                        console.error("Ошибка при загрузке tributes:", err);
                      }
                    } catch (err) {
                      console.error("Ошибка при загрузке данных:", err);
                    }
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Отклонить
                </Button>
                <Button
                  onClick={handleConfirmTribute}
                  fullWidth
                  data-status="approved"
                  data-tributeId={el.uuid}
                >
                  Принять
                </Button>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
}

export default Tributes
